import { baseApi, useDeleteIntegrationMutation } from 'src/services';
import { useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { useParams } from 'react-router-dom';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { CurrentIntegrationCard } from './CurrentIntegrationCard';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { INTEGRATION_ACCESS_ROLES } from 'src/constants/roles';
import { useAppDispatch } from 'src/store';
import type { IntegrationCatalogEntry } from 'src/components/Integrations/IntegrationTypes';
import type { ProjectIntegration } from 'src/services/projectsService';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

// The integration types the control desk shows a card for.
const INTEGRATION_CARD_TYPES = new Set([
  'zendesk_chat',
  'zendesk_email',
  'zoho_salesiq',
  'zoho_desk',
  'freshdesk_email',
  'freshdesk_chat',
]);

// The setup step Launch opens at, for the integrations with a setup flow.
const getLaunchStep = ({ integration_type, integration_data }: ProjectIntegration) => {
  switch (integration_type) {
    case 'zendesk_chat':
      return integration_data?.author_display_name ? 4 : 3;
    case 'zendesk_email':
      return integration_data?.auth_type === 'oauth' &&
        integration_data?.auth_status === 'needs_reauthorization'
        ? 2
        : 3;
    case 'freshdesk_email':
      return 3;
    default:
      return undefined;
  }
};

interface CurrentIntegrationProps {
  integration: ProjectIntegration;
  integrationTypes?: IntegrationCatalogEntry[];
}

export const CurrentIntegration = ({
  integration,
  integrationTypes = [],
}: CurrentIntegrationProps) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const dispatch = useAppDispatch();
  const integrationTypeConfig =
    integrationTypes.find(
      (integrationType) => integrationType.data.integration_type === integration.integration_type,
    ) || {};
  const [deleteIntegration, { isLoading: isLoadingDeleteIntegration }] =
    useDeleteIntegrationMutation();
  const deleteIntegrationHandler = (integrationId: number) => {
    deleteIntegration({ projectId: id, integrationId }).then((deleteIntegrationResult) => {
      dispatch(baseApi.util.invalidateTags(['Projects']));

      if (!('error' in deleteIntegrationResult)) {
        notification.success('Integration Deleted.');
        dispatch(setSelectedIntegration(null));
      }
    });
  };
  const { data: userInfo } = useGetUsersMeQuery();
  const disabledByPermision = !INTEGRATION_ACCESS_ROLES.includes(userInfo?.role);

  useLoaderWhile(isLoadingDeleteIntegration);

  useEffect(() => {
    if (integration) {
      loader.hide();
    }
  }, [integration]);

  const launchStep = getLaunchStep(integration);

  return INTEGRATION_CARD_TYPES.has(integration.integration_type) ? (
    <CurrentIntegrationCard
      integrationTypeConfig={integrationTypeConfig}
      integration={integration}
      deleteIntegrationHandler={deleteIntegrationHandler}
      disabledByPermision={disabledByPermision}
      launchStep={launchStep}
      keepSelectionInSync={integration.integration_type === 'zendesk_email'}
    />
  ) : undefined;
};
